import { FC } from 'react'
import Select from 'react-select'
import type { CSSObjectWithLabel } from 'react-select'
import { Autocomplete, Box, InputAdornment, TextField } from '@mui/material'
import { selectCustomStyles } from '~/shared/ui'
import { AssigneeGroupOption } from './userGroups'
import BlockIcon from '@mui/icons-material/Block'
import { PatientUser } from '~/shared/types'
import UserGroupAvatar from './UserGroupAvatar'
import AccountCircle from '@mui/icons-material/AccountCircle'

const FILL_STYLES = {
  ...selectCustomStyles,
  container: (base: CSSObjectWithLabel) => ({ ...base, width: '100%', height: '100%' }),
}

interface Props {
  selectedValue: AssigneeGroupOption | null
  options: AssigneeGroupOption[]
  patient?: PatientUser | null
  onChange: (value: AssigneeGroupOption) => void
  disabled?: boolean
  detailed?: boolean
  /** Fill the container: the detailed picker instead of its 180px default, the plain select in both dimensions. */
  fullWidth?: boolean
  groupedOptions?: {
    label: string
    options: AssigneeGroupOption[]
  }[]
}

const UserGroupSelect: FC<Props> = props => {
  const formatOptionLabel = (
    option: AssigneeGroupOption,
    optionProps?: React.HTMLAttributes<HTMLElement>,
  ) => {
    return (
      <Box {...optionProps} key={option.value}>
        {props.detailed ? (
          option.value ? (
            <UserGroupAvatar
              id={option.value}
              patientId={props?.patient?.id}
              size="small"
              avatarStyle={{ margin: 0, display: 'inline-flex' }}
            />
          ) : null
        ) : null}
        <Box sx={{ paddingLeft: 1 }}>{option.label}</Box>
        {option.disabledReason && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Box>
              <BlockIcon />
            </Box>
            <Box sx={{ paddingLeft: 1 }}>{option.disabledReason}</Box>
          </Box>
        )}
      </Box>
    )
  }

  if (props.detailed) {
    return (
      <Autocomplete
        size="small"
        forcePopupIcon={false}
        slotProps={{
          paper: {
            sx: { width: '150%' },
          },
        }}
        className="classToStopPropagation"
        style={{ width: props.fullWidth ? '100%' : 180 }}
        disabled={props.disabled}
        getOptionDisabled={(option: AssigneeGroupOption) => !!option.disabledReason}
        getOptionLabel={(option: AssigneeGroupOption) => option?.label || ''}
        options={props.options}
        groupBy={option => option.category}
        selectOnFocus={true}
        renderInput={params => {
          return (
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <TextField
                {...params}
                size="small"
                sx={{
                  '& fieldset': { border: 'none' },
                  '&:hover': props.disabled
                    ? {}
                    : { backgroundColor: '#f5f5f5', borderRadius: '10px' },
                }}
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    startAdornment: (
                      <InputAdornment position="start">
                        {props.selectedValue?.value ? (
                          <UserGroupAvatar
                            id={props.selectedValue.value}
                            patientId={props?.patient?.id}
                            size="small"
                            avatarStyle={{ margin: 0, display: 'inline-flex' }}
                          />
                        ) : props.disabled ? null : (
                          <AccountCircle />
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
          )
        }}
        value={props.selectedValue ?? null}
        renderOption={({ key: _, ...optionProps }, option) => {
          return formatOptionLabel(option, optionProps)
        }}
        onChange={(event, newValue) => {
          if (newValue) {
            props.onChange(newValue)
          }
        }}
      />
    )
  }

  return (
    <Select
      styles={props.fullWidth ? FILL_STYLES : selectCustomStyles}
      isDisabled={props.disabled}
      isOptionDisabled={(option: AssigneeGroupOption) => !!option.disabledReason}
      onChange={newValue => {
        if (newValue) {
          props.onChange(newValue)
        }
      }}
      options={props.groupedOptions}
      value={props.selectedValue}
      placeholder={'Select Assignee'}
      menuPlacement="auto"
      menuPortalTarget={document.body}
      formatOptionLabel={option => formatOptionLabel(option)}
    />
  )
}

export default UserGroupSelect
